import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FiEye, FiEyeOff } from 'react-icons/fi';

import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '@/services/authService';
import ErrorModal from '@/components/common/ErrorModal';
// SpaceSelector 모달 제거 - /select-space 페이지로 대체
import { useNavigationStore } from '@/common/store';
import { useAuthStore } from '@/store/useAuthStore';
import Config from '@/config';
import { LoginUserInfo } from '@/common/types/login';


import FactsheetLogo from '@/common/assets/logo/factsheet_logo.png';
import { Input } from '@/components/ui/Input';
import SimpleCertifycationModal from '@/components/SimpleCertifycationModal';
import InisisAuthButton, { InisisAuthResult } from '@/components-common/inisis/InisisAuthButton';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { Button } from '@/common/components/button/Button';
import TermsModal from '@/pages/SignIn/components/TermsModal';
import LoginHeroSection from './components/LoginHeroSection';
interface LoginForm {
  loginId: string;
  password: string;
  autoSignIn: boolean;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {  } = useNavigationStore();
  const { user } = useAuthStore();
  const forgotType = useRef<'id' | 'password' | undefined>(undefined);
  const [step, setStep] = useState<'login'| 'forgotId' | 'forgotPassword'>('login');
  const [form, setForm] = useState<LoginForm>({
    loginId: '',
    password: '',
    autoSignIn: false,
  });
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [openCertifyPopup, setOpenCertifyPopup] = useState(false);
  const [isCertifiedPersonal, setIsCertifiedPersonal] = useState(false);
  const [termsModalType, setTermsModalType] = useState<'terms' | 'privacy' | 'marketing' | null>(null);


  const getSpaceUrl = (type: ('STARTUP' | 'INVESTOR' | 'INNOVATE'), companyCode: string): string => {
    const urlMap: Record<('STARTUP' | 'INVESTOR' | 'INNOVATE'), string> = {
      STARTUP: `${Config.startupClientUrl}/${companyCode}/company-info`,
      INVESTOR: `${Config.investorClientUrl}/${companyCode}/home`,
      INNOVATE: `${Config.innovateClientUrl}/${companyCode}/program/list`,
    };

    return urlMap[type];
  };

  const getSingleSpaceRedirectUrl = (loginUser: LoginUserInfo | null): string | null => {
    if (!loginUser || loginUser.companyUsers.length !== 1) {
      return null;
    }

    const [companyUser] = loginUser.companyUsers;

    if (!companyUser.emailVerified) {
      return null;
    }

    const services = companyUser.company.service;

    if (services.length !== 1) {
      return null;
    }

    const companyCode = companyUser.company.code;

    localStorage.setItem('lastSelectedCompanyCode', companyCode);
    localStorage.setItem('lastSelectedSpace', services[0]);

    return getSpaceUrl(services[0], companyCode);
  };

  const moveAfterLogin = (loginUser: LoginUserInfo | null) => {
    const singleSpaceRedirectUrl = getSingleSpaceRedirectUrl(loginUser);

    if (singleSpaceRedirectUrl) {
      window.location.href = singleSpaceRedirectUrl;
      return;
    }

    navigate('/my-space');
  };

  const validateForm = (): boolean => {
    if (!form.loginId.trim()) {
      setError('아이디를 입력해주세요.');
      return false;
    }
    if (!form.password.trim()) {
      setError('비밀번호를 입력해주세요.');
      return false;
    }
    return true;
  };
  const handleCertify = async (certifyData: InisisAuthResult) => {

    if(!certifyData || !certifyData.userDi || certifyData.userDi.length === 0) {
      setError('인증에 실패했습니다.');
      setIsErrorModalOpen(true);
      return;
    }

    const result = await authService.loginWithUserDi(certifyData.userDi, certifyData.userCi, certifyData.token);
    if(result && result.success) {
      const profileResult = await authService.setUserProfile();
      moveAfterLogin(profileResult.data ?? useAuthStore.getState().user);
    } else {
      setError(result?.message || '인증이 실패 하였습니다.')  ;
      setIsErrorModalOpen(true);
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prevForm => ({
      ...prevForm,
      [name]: value
    }));
  };

  const handleLogin = async () => {
    
    if (!validateForm()) {
      setIsLoading(false);
      setIsErrorModalOpen(true);
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const result = await authService.login(form);
      const redirectURL = searchParams.get('redirectUrl');
      console.log(result);

      if(redirectURL){
        window.location.replace(redirectURL);
      } else if(result && result.success) {
        moveAfterLogin(useAuthStore.getState().user);
      }       
    } catch (err: any) {
      setError(err.message || '로그인에 실패했습니다.');
      setIsErrorModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container>
      <LoginContainer>
        {step === 'login' && (
          <LoginSection>
            <LoginBox>
            <Logo src={FactsheetLogo} alt="Factsheet Logo" />
            <FormGroup style={{gap: 7}} >
              <LoginLabel>로그인</LoginLabel>
              <LoginDescription>어떤 정보로 입력해도 계정은 그대로</LoginDescription>
            </FormGroup>

            <FormGroup style={{gap: 16}} >
              <FormGroup>
                <Label className='title-medium-16' htmlFor="loginId">ID</Label>
                <Input
                  id="loginId"
                  type="text" 
                  
                  placeholder="아이디를 입력해 주세요." 
                  name="loginId" 
                  value={form.loginId} 
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    if(e.key === 'Enter'){
                      if(form.loginId.trim() && form.password.trim()){
                        handleLogin();
                      }
                    }
                  }}
                />
              </FormGroup>

              <FormGroup>
                <Label className='title-medium-16' htmlFor="password">Password</Label>
                <PasswordInputWrapper>
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="비밀번호를 입력해 주세요."
                    name="password"
                    value={form.password}
                    onChange={handleInputChange}
                    onKeyDown={(e) => {
                      if(e.key === 'Enter'){
                        if(form.loginId.trim() && form.password.trim()){
                          handleLogin();
                        }
                      }
                    }}
                  />
                  <PasswordToggle type="button" onClick={togglePasswordVisibility}>
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </PasswordToggle>
                </PasswordInputWrapper>
              </FormGroup>
              <Button $variant='gradient' $size='md' $isFull disabled={isLoading} onClick={(e) => {
                e.preventDefault();
                handleLogin();
              }}>
                {isLoading ? '로그인 중...' : '로그인'}
              </Button>
              
              <SimpleDivider>
                <DividerLine />
                <DividerText>또는</DividerText>
                <DividerLine />
              </SimpleDivider>
              
              <SimpleAuthSection>
                <SimpleAuthGuide>
                  <SimpleAuthIcon>💡</SimpleAuthIcon>
                  <SimpleAuthText>
                    아이디나 비밀번호가 기억나지 않으신가요?<br />
                    <strong>간편 인증</strong>으로 바로 로그인하세요!
                  </SimpleAuthText>
                </SimpleAuthGuide>
                <InisisAuthButton
                  $variant='gradient'
                  $size='md'
                  $title='간편 인증으로 로그인'
                  onSuccess={(data: InisisAuthResult) => {
                    handleCertify(data);
                  }}
                />
              </SimpleAuthSection>
            </FormGroup>

            <SignUpSection>
              팩트시트가 처음이세요? 
              <SignUpLink onClick={() => {
                window.location.href = '/signin/signup';
              }}>
                회원가입하기
              </SignUpLink>
            </SignUpSection>
            
            <Footer>
              <FooterTop>
                <FooterLink onClick={() => setTermsModalType('terms')}>이용약관</FooterLink>
                <FooterDot />
                <FooterLink onClick={() => setTermsModalType('privacy')}>개인정보처리방침</FooterLink>
                <FooterDot />
                <FooterText>info@factsheet.biz</FooterText>
                <FooterDot />
                <FooterText>유선번호 : 1877-6503</FooterText>
              </FooterTop>
              <FooterBottom>
                <FooterText>© {new Date().getFullYear()}주식회사 애즈플로우</FooterText>
                <FooterDot />
                <FooterText>대표 : 함세희</FooterText>
                <FooterDot />
                <FooterText>사업자번호 : 167-86-03565</FooterText>
                <FooterDot />
                <FooterText>통신판매업신고번호 : 제 2026-서울강남-01056호</FooterText>
              </FooterBottom>
              <FooterAddress>주소 : 서울특별시 강남구 봉은사로 435, 5층 505호 (KTOA)</FooterAddress>
            </Footer>
            
            <ErrorModal
              isOpen={isErrorModalOpen}
              message={error}
              onClose={() => setIsErrorModalOpen(false)}
            />
          </LoginBox>
        </LoginSection>
        )}

        <HeroSectionWrapper>
          <LoginHeroSection />
        </HeroSectionWrapper>

        {openCertifyPopup && (
          <SimpleCertifycationModal
            openCertifyPopup={openCertifyPopup}
            setOpenCertifyPopup={setOpenCertifyPopup}
            handleCertify={handleCertify}
          />
        )}

        {termsModalType && (
          <TermsModal
            isOpen={!!termsModalType}
            onClose={() => setTermsModalType(null)}
            type={termsModalType}
          />
        )}
      </LoginContainer>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${semanticColors.background.primary};
  padding: 16px;
  
  @media (max-width: 768px) {
    padding: 0;
  }
`;

const LoginContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
  max-width: 1490px;
  max-height: 904px;
  background-color: ${semanticColors.background.secondary};
  border-radius: 21px;
  padding: 9px 9px 9px 0px;
  overflow: hidden;
  
  @media (max-height: 800px) {
    max-height: 100%;
    overflow-y: auto;
  }
  
  @media (max-width: 1024px) {
    max-height: none;
    flex-direction: column;
    padding: 9px;
    overflow-y: auto;
  }
  
  @media (max-width: 768px) {
    border-radius: 0;
    padding: 0;
  }
`;

const LoginSection = styled.div`
  width: 100%;
  max-width: calc(62px + 371px + 95px);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px 0;
  
  @media (max-height: 800px) {
    padding: 16px 24px;
    align-items: flex-start;
  }
  
  @media (max-width: 1024px) {
    max-width: 100%;
    padding: 40px 24px;
  }
  
  @media (max-width: 768px) {
    padding: 32px 20px;
    min-height: 100vh;
    align-items: flex-start;
    padding-top: 60px;
  }
`;

const LoginLabel = styled.h2`
  ${typography.heading.h2}
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const LoginDescription = styled.span`
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.secondary};
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const LoginBox = styled.div`
  width: 100%;
  max-width: 371px;
  display: flex;
  flex-direction: column;
  gap: 48px;
  
  @media (max-height: 800px) {
    gap: 24px;
  }
  
  @media (max-height: 700px) {
    gap: 16px;
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    gap: 32px;
  }
`;

const Logo = styled.img`  
  width: 124px; 
  height: auto;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  &.row {
    flex-direction: row;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: ${semanticColors.text.primary};
`;

const PasswordInputWrapper = styled.div`
  position: relative;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${semanticColors.text.tertiary};
  cursor: pointer;
  padding: 0.25rem;

  &:hover {
    color: ${semanticColors.text.primary};
  }
`;

const SignUpSection = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.secondary};
`;

const SignUpLink = styled.span`
  color: ${semanticColors.background.brand};
  cursor: pointer;
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 2px;
  
  &:hover {
    color: ${semanticColors.background.brandHover};
    text-decoration: none;
  }
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: 100%;
  margin-top: auto;
  padding-top: 16px;
  
  @media (max-height: 800px) {
    margin-top: 0;
    padding-top: 8px;
  }
  
  @media (max-width: 768px) {
    padding-top: 24px;
    gap: 8px;
  }
`;

const FooterTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const FooterBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 4px;
  }
`;

const HeroSectionWrapper = styled.div`
  flex: 1;
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const FooterLink = styled.span`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  cursor: pointer;
  
  &:hover {
    color: ${semanticColors.text.secondary};
    text-decoration: underline;
  }
`;

const FooterText = styled.span`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
`;

const FooterDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background-color: ${semanticColors.text.tertiary};
  opacity: 0.5;
`;

const FooterAddress = styled.div`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  opacity: 0.7;
  text-align: center;
`;

const SimpleDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 8px 0;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: ${semanticColors.border.primary};
`;

const DividerText = styled.span`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
`;

const SimpleAuthSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SimpleAuthGuide = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.12) 100%);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
`;

const SimpleAuthIcon = styled.span`
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 1px;
`;

const SimpleAuthText = styled.p`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.secondary};
  line-height: 1.5;
  margin: 0;
  
  strong {
    color: #6366f1;
    font-weight: 600;
  }
`;

export default Login;
